import { Value } from '@udecode/plate-common';
import { Bundle, BundledItem, PurchaseOrderBundle, PurchaseOrderDeliveryOption } from '../contracts/catalog/bundles';
import { APIError } from '../errors/APIError';
import { useAPIMutation, useAPIQuery } from '../useAPIProvider';

export const useBundles = (partnerId: string, companyId: string) => {
  return useAPIQuery(['getBundles', companyId], async client => {
    const response = await client.catalog.catalog.getBundles({
      params: {
        partnerId,
        companyId
      }
    });
    if (response.status !== 200) throw new Error('There is an error');
    return response.body.items.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  });
};

export const usePurchaseOrderBundle = (partnerId: string, companyId: string) => {
  return useAPIQuery(['getPurchaseOrderBundle', companyId], async client => {
    const response = await client.catalog.catalog.getPurchaseOrderBundles({
      params: {
        partnerId,
        companyId
      }
    });
    if (response.status !== 200) throw new Error('There is an error');
    return response.body.items;
  });
};

interface DeleteBundleArgs {
  mspId: string;
  customerId: string;
  id: string;
}
export const useDeleteBundle = () => {
  return useAPIMutation((client, query) => async ({ id, mspId, customerId }: DeleteBundleArgs) => {
    const response = await client.catalog.catalog.deleteBundle({
      params: { id, mspId, customerId }
    });

    if (response.status !== 200) {
      throw new Error('Failed to delete bundle');
    }

    await query.invalidateQueries(['getBundles', customerId]);
  });
};

export interface CreateBundleArgs {
  customerId: string;
  mspId: string;
  name: string;
  description: string;
  mainDevice?: string;
  bundledItems: BundledItem[];
}

export const useCreateBundle = () => {
  return useAPIMutation((client, query) => async ({ mspId, customerId, ...args }: CreateBundleArgs) => {
    const codes = await client.catalog.catalog.getCodesForBundleNames({
      body: {
        names: [args.name]
      }
    });

    if (codes.status !== 200) {
      throw new Error('Failed to create bundle codes');
    }

    const [code] = codes.body.items;

    const response = await client.catalog.catalog.createBundle({
      params: {
        mspId,
        customerId
      },
      body: {
        ...args,
        code: code.code
      }
    });
    if (response.status !== 200) {
      throw new Error('Failed to create bundle');
    }

    await query.invalidateQueries(['getBundles', customerId]);
    return response.body;
  });
};

export interface EditBundleArgs {
  id: string;
  customerId: string;
  mspId: string;
  name: string;
  description: string;
  mainDevice?: string;
  bundledItems: BundledItem[];
}

export const useEditBundle = () => {
  return useAPIMutation((client, query) => async ({ mspId, customerId, id, ...args }: EditBundleArgs) => {
    const bundles = query.getQueryData(['getBundles', customerId]) as Bundle[];
    if (!bundles) throw new Error('Missing bundles');

    const bundle = bundles.find(bundle => bundle.id === id);
    if (!bundle) throw new Error(`Missing bundle with id ${id}`);

    const response = await client.catalog.catalog.editBundle({
      params: {
        mspId,
        customerId,
        id
      },
      body: {
        ...args,
        code: bundle.code
      }
    });

    if (response.status !== 200) {
      throw new Error('Failed to edit bundle');
    }

    await query.invalidateQueries(['getBundles', customerId]);
    return response.body;
  });
};

export const usePurchaseOrderBundles = (partnerId: string, companyId: string) => {
  return useAPIQuery(['getPurchaseOrderBundles', partnerId, companyId], async client => {
    const response = await client.catalog.catalog.getPurchaseOrderBundles({
      params: {
        partnerId,
        companyId
      }
    });
    if (response.status !== 200) throw APIError.fromResponse(response);
    return response.body.items.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  });
};

export interface CreatePurchaseOrderArgs {
  customerId: string;
  mspId: string;
  addressId?: string;
  contactDetailId?: string;
  deliveryOption: PurchaseOrderDeliveryOption;
  name: string;
  state: number;
  bundledItems: BundledItem[];
  poReference?: string;
  stockingOrderNumber?: string;
  partnerReference?: string;
  financeModelIds?: string[];
  peripheralFinanceModelId?: string;
  deviceFeeCatalogItemId?: string;
  notes?: {
    value: Value;
  };
}

export const useCreatePurchaseOrderBundle = () => {
  return useAPIMutation((client, query) => async ({ customerId, mspId, ...args }: CreatePurchaseOrderArgs) => {
    const codes = await client.catalog.catalog.createCodesForPurchaseOrderBundles({
      body: {
        names: [args.name]
      }
    });

    if (codes.status !== 200) throw APIError.fromResponse(codes);

    const [code] = codes.body.items;

    const response = await client.catalog.catalog.createPurchaseOrderBundle({
      params: {
        mspId,
        customerId
      },
      body: {
        code: code.code,
        ...args
      }
    });

    if (response.status !== 200) throw APIError.fromResponse(response);

    await query.invalidateQueries(['getPurchaseOrderBundles', mspId, customerId]);
    return response.body;
  });
};

export interface UpdatePurchaseOrderArgs extends CreatePurchaseOrderArgs {
  id: string;
}

export const useUpdatePurchaseOrderBundle = () => {
  return useAPIMutation((client, query) => async ({ id, customerId, mspId, ...args }: UpdatePurchaseOrderArgs) => {
    const bundles = query.getQueryData(['getPurchaseOrderBundles', mspId, customerId]) as PurchaseOrderBundle[];
    if (!bundles) throw new Error('Missing bundles');

    const bundle = bundles.find(bundle => bundle.id === id);
    if (!bundle) throw new Error(`Missing bundle with id ${id}`);

    const response = await client.catalog.catalog.updatePurchaseOrderBundle({
      params: {
        mspId,
        customerId,
        id
      },
      body: {
        ...args,
        code: bundle.code
      }
    });

    if (response.status !== 200) {
      throw new Error('Failed to edit bundle');
    }

    await query.invalidateQueries(['getPurchaseOrderBundles', mspId, customerId]);
    return response.body;
  });
};

interface DeletePurchaseOrderBundleArgs {
  mspId: string;
  customerId: string;
  id: string;
}
export const useDeletePurchaseOrderBundle = () => {
  return useAPIMutation((client, query) => async ({ id, mspId, customerId }: DeletePurchaseOrderBundleArgs) => {
    const response = await client.catalog.catalog.deletePurchaseOrderBundle({
      params: { mspId, customerId, id }
    });

    if (response.status !== 200) {
      throw new Error('Failed to delete bundle');
    }

    await query.invalidateQueries(['getPurchaseOrderBundles', mspId, customerId]);
  });
};

interface UpdatePurchaseOrderBundleStateArgs {
  mspId: string;
  customerId: string;
  id: string;
  state: number;
}
export const useUpdatePurchaseOrderBundleState = () => {
  return useAPIMutation(
    (client, query) =>
      async ({ id, customerId, mspId, state }: UpdatePurchaseOrderBundleStateArgs) => {
        const response = await client.catalog.catalog.updatePurchaseOrderBundleState({
          params: {
            mspId,
            customerId,
            id
          },
          body: {
            state
          }
        });

        if (response.status !== 200) {
          throw new Error('Failed to update bundle state');
        }

        await query.invalidateQueries(['getPurchaseOrderBundles', mspId, customerId]);
        return response.body;
      }
  );
};
